import { cloneDeep } from 'lodash-es';
import { BetDateType, Currency } from '@/constants';
import { todayDateRangeInUserTimeZone } from '@/helpers';
import { applyDatePeriodPresetToBetDate } from '@/helpers/applyDatePeriodPresetToBetDate';
import { createOptionalSportFilter, resetOptionalSportFilter } from '@/helpers/sportFilter';
import { BookBalanceItem } from '@/models/bookBalance';
import { createBookBalanceTableHeaders } from '@/pages/accounting/bookBalance/BookBalanceTableHeaders';
import i18n from '@/plugins/i18n';
import { isRequestCanceled } from '@/services/baseService';
import { bookBalanceService } from '@/services/bookBalanceService';
import { createKeyedLoadingGenerationTracker, createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { useUserStore } from '@/stores/user';
import { IBookBalanceFilters } from '@/types/bookBalance';
import { IPageOptions } from '@/types/page';
import type { DataTableHeader } from '@/types/vuetify';

function createBookBalanceStoreSetup(storeId: string) {
    // --- state ---
    const t = i18n.global.t;

    const itemsByCurrency = reactive<Record<string, BookBalanceItem[]>>({
        total: [],
        eur: [],
        brl: [],
        usd: [],
        gbp: [],
    });

    const loading = ref(false);
    const aggregateLoadGeneration = createLoadingGenerationTracker();
    const currencyLoadGeneration = createKeyedLoadingGenerationTracker();
    const loadingByCurrency = reactive<Record<string, boolean>>({
        total: false,
        eur: false,
        brl: false,
        usd: false,
        gbp: false,
    });
    const isExporting = ref(false);

    // --- filters ---
    const filters = reactive<IBookBalanceFilters>({
        sportId: createOptionalSportFilter(),
        betDateType: {
            value: BetDateType.PlacementGMT,
            title: 'bets.betDateType',
            allowSaveState: true,
            disableClear: true,
        },
        datePeriod: {
            value: 'range',
            title: 'bookBalance.datePeriod.title',
            allowSaveState: true,
            disableClear: true,
        },
        betDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'bookBalance.betDateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        betBookmakerId: {
            value: null,
            title: 'bets.bookmaker',
            allowSaveState: true,
        },
        betTradingTypeId: {
            value: null,
            title: 'bets.tradingType',
            allowSaveState: true,
        },
        betEventPeriodId: {
            value: null,
            title: 'bets.periodType',
            allowSaveState: true,
        },
        betEventType: {
            value: null,
            title: 'common.marketType',
            allowSaveState: true,
        },
        userAgentId: {
            value: null,
            title: 'bets.agent',
            allowSaveState: true,
        },
        betPlatformId: {
            value: null,
            title: 'bets.platform',
            allowSaveState: true,
        },
        userObserveId: {
            value: null,
            title: 'bets.userObserveId',
            allowSaveState: true,
        },
        betSelectionId: {
            value: null,
            title: 'bets.betType',
            allowSaveState: true,
        },
        userId: {
            value: null,
            title: 'bets.player',
            allowSaveState: true,
        },
        customLeagueLimitsCategoryId: {
            value: null,
            title: 'pregame.leagueLimitsCategory',
            allowSaveState: true,
        },
        betBetVsOpen: {
            value: null,
            title: 'bets.betVsOpen',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const userStore = useUserStore();
    const filterPanel = useFilterPanelState(filters);
    const availableFilters = computed(() => filterPanel.availableFilters.value.filter((filter) => filter.key !== 'betBetVsOpen' || userStore.isAdmin()));
    const visibleFilters = computed(() => filterPanel.visibleFilters.value.filter((filterName) => filterName !== 'betBetVsOpen' || userStore.isAdmin()));
    const { hiddenFilters, visibleFiltersMobile, filterOrder, useButtonForFiltersApply, saveFiltersState } = filterPanel;

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [],
    });

    // --- reset ---
    function resetFilters() {
        resetOptionalSportFilter(filters.sportId);
        filters.betDateType.value = BetDateType.PlacementGMT;
        filters.datePeriod.value = 'range';
        filters.betDate.value = todayDateRangeInUserTimeZone();
        filters.betBookmakerId.value = null;
        filters.betTradingTypeId.value = null;
        filters.betEventPeriodId.value = null;
        filters.betEventType.value = null;
        filters.userAgentId.value = null;
        filters.betPlatformId.value = null;
        filters.userObserveId.value = null;
        filters.betSelectionId.value = null;
        filters.userId.value = null;
        filters.customLeagueLimitsCategoryId.value = null;
        filters.betBetVsOpen.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createBookBalanceTableHeaders());

    const availableColumns = computed(() => headers.value.filter((item) => item.title));

    const hiddenColumns = ref<string[]>([]);

    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'bookBalance',
        filters,
        saveFiltersState,
        resetFilters,
    });

    async function loadData(currencyId: number | null = null) {
        const currencyKey =
            currencyId === null
                ? 'total'
                : currencyId === Currency.Euro
                  ? 'eur'
                  : currencyId === Currency.Brl
                    ? 'brl'
                    : currencyId === Currency.Usd
                      ? 'usd'
                      : currencyId === Currency.Gbp
                        ? 'gbp'
                        : 'total';

        const currencyLoadId = currencyLoadGeneration.next(currencyKey);
        loadingByCurrency[currencyKey] = true;
        try {
            let filtersCopy = cloneDeep(filters);
            applyDatePeriodPresetToBetDate(filtersCopy);

            const params = {
                filters: filtersCopy,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Site', 'CurrencyAll', 'Agent', 'Platform'],
                extraParams: {
                    currencyId: currencyId,
                },
                requestKey: `book-balance:${currencyKey}`,
            };

            const response = await bookBalanceService.list(params);
            itemsByCurrency[currencyKey] = response.data.rows.map((item: any) => new BookBalanceItem(item));
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (currencyLoadGeneration.isLatest(currencyKey, currencyLoadId)) {
                loadingByCurrency[currencyKey] = false;
            }
        }
    }

    // --- actions ---
    async function loadAllData() {
        const aggregateLoadId = aggregateLoadGeneration.next();
        loading.value = true;
        try {
            await Promise.all([
                loadData(null), // TOTAL
                loadData(Currency.Euro), // EUR
                loadData(Currency.Brl), // BRL
                loadData(Currency.Usd), // USD
                loadData(Currency.Gbp), // GBP
            ]);
        } finally {
            if (aggregateLoadGeneration.isLatest(aggregateLoadId)) {
                loading.value = false;
            }
        }
    }

    const exportColumns = {
        site_id: t('bookBalance.bookmaker'),
        account_currency_id: '',
        bet_count: t('bookBalance.betCount'),
        settled_stake_pc_sum: t('bookBalance.staked'),
        abs_balance_pc: t('bookBalance.turnover'),
        abs_balance_agent_pc: t('bookBalance.agTurnover'),
        settled_stake_bookmaker_pc_sum: t('bookBalance.bookStaked'),
        abs_balance_bookmaker_pc: t('bookBalance.bookTurnover'),
        company_com_pc: t('bookBalance.bookComm'),
        book_fees_ac: t('bookBalance.bookFees'),
        book_rebate_ac: t('bookBalance.feesRebate'),
        settled_stake_position_pc_sum: t('bookBalance.position'),
        pt_balance_pc: t('bookBalance.coPositionBalance'),
        balance_bookmaker_pc: t('bookBalance.bookBalance'),
        balance_agent_pc: t('bookBalance.agBalance'),
        balance_pc: t('bookBalance.playerBalance'),
        player_yield: t('bookBalance.playerYield'),
        player_com_pc: t('bookBalance.payComms'),
        book_total_pc: t('bookBalance.bookTotal'),
        company_pl_pc: t('bookBalance.companyPL'),
    };

    async function exportData() {
        isExporting.value = true;
        try {
            let filtersCopy = cloneDeep(filters);
            applyDatePeriodPresetToBetDate(filtersCopy);

            const params: any = {
                filters: filtersCopy,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: 1,
                    itemsPerPage: 100,
                },
                requiredData: [],
                requiredDictionaries: [],
                columns: JSON.stringify(exportColumns),
            };

            const response = await bookBalanceService.export(params);

            let filename = 'book-balance.xlsx';
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export error:', e);
            throw e;
        } finally {
            isExporting.value = false;
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadAllData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadAllData();
        }
    }

    function clearData() {
        itemsByCurrency.total = [];
        itemsByCurrency.eur = [];
        itemsByCurrency.brl = [];
        itemsByCurrency.usd = [];
        itemsByCurrency.gbp = [];
        options.value.page = 1;
        loading.value = false;
        loadingByCurrency.total = false;
        loadingByCurrency.eur = false;
        loadingByCurrency.brl = false;
        loadingByCurrency.usd = false;
        loadingByCurrency.gbp = false;
    }

    // --- return ---
    return {
        storeId,
        itemsByCurrency,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        loadingByCurrency,
        options,
        loadAllData,
        updateOptions,
        clearData,
        isExporting,
        exportData,
        stats: undefined,
        hasExport: true,
        hasRefresh: true,
    };
}

export function useBookBalanceStore(storeId: string) {
    const useStore = defineStore(storeId, () => createBookBalanceStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'bookBalance'),
    });
    return useStore();
}

export type BookBalanceStoreSetup = ReturnType<typeof useBookBalanceStore>;
